import { Controller } from "@hotwired/stimulus";
import { captureMessage } from "@sentry/browser";

import { BrowserExtension } from "~/lib/browser-extension";
import logger from "~/lib/logger";

const log = logger({ name: "ExtensionStatusController" });

type Status =
  | "checking"
  | "not-installed"
  | "not-authenticated"
  | "upgrade-required"
  | "error"
  | "success";

export default class ExtensionStatusController extends Controller {
  static override targets = ["status"];

  static override values = {
    minVersion: String,
    suppressErrors: Boolean,
  } as const;
  declare readonly minVersionValue: string;
  declare readonly suppressErrorsValue: boolean;
  declare readonly statusTargets: HTMLElement[];

  private extension!: BrowserExtension;

  override connect(): void {
    this.extension = new BrowserExtension();
    this.checkExtensionStatus();
  }

  public async openPopup() {
    return this.extension.openPopup();
  }

  private async checkExtensionStatus(): Promise<void> {
    this.showStatus("checking");

    const status = await this.extension.status({
      minVersion: this.minVersionValue,
    });
    this.showStatus(status);
  }

  private resolveStatus(status: Status): Status {
    if (!this.suppressErrorsValue) return status;
    if (status === "upgrade-required") return "success";
    if (status === "error") return "not-authenticated";
    return status;
  }

  private showStatus(status: Status): void {
    log.debug("show status:", status);
    this.hideAllStatuses();

    const statusElement = this.statusTargets.find(
      (el) => el.dataset.status === this.resolveStatus(status),
    );
    if (statusElement) {
      statusElement.classList.remove("hidden");
    } else {
      captureMessage("Missing status element", {
        level: "error",
        extra: { status },
      });
      // Avoid an infinite loop if there's no error element
      if (status !== "error") {
        this.showStatus("error");
      }
    }
  }

  private hideAllStatuses(): void {
    this.statusTargets.forEach((element) => {
      element.classList.add("hidden");
    });
  }
}
